import { Carousel } from "@faire/deprecated-retailer-visitor-shared/components/Carousel/Carousel";
import { useGetFaireAcademyPageState } from "@faire/deprecated-retailer-visitor-shared/hooks/openWithFaire/useGetFaireAcademyPageState";
import { useGetStoreType } from "@faire/deprecated-retailer-visitor-shared/hooks/openWithFaire/useGetStoreType";
import { AcademyGuideCardSmall } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/components/GuideCards/AcademyGuideCardSmall";
import { useGuidesWithLikes } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/academy/useGuidesWithLikes";
import { ExploreAllGuidesButton } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/components/OpenUpShopWithConfidence/ExploreAllGuidesButton";
import { PickUpWhereYouLeftOff } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/components/OpenUpShopWithConfidence/PickUpWhereYouLeftOff";
import { OWF_SECTION_NAME } from "@faire/deprecated-retailer-visitor-shared/views/open-with-faire/OpenWithFaireFaireAcademyLandingPage/types";
import { useQuery as useLandingPageDataQuery } from "@faire/web-api--source/endpoints/www/api/faire-academy/landing-page-data/get-hooks";
import { trackOpenWithFaireIndividualGuideCtaClick } from "@faire/web-api--source/events/openWithFaire/click/individualGuideCta";
import { useViewport } from "@faire/web/common/hooks/useViewport";
import { StoreType } from "@faire/web/common/StoreType";
import { Flex, Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { SlideIn } from "@faire/web/ui/SlideIn/SlideIn";
import React from "react";
import styled from "styled-components";

export const FaireAcademyContent: React.FC = () => {
  const { data } = useLandingPageDataQuery();

  if (data?.progress?.guide) {
    return (
      <SlideIn offset="50px">
        <PickUpWhereYouLeftOff
          guide={data.progress.guide}
          chaptersRead={data.progress.read_chapters_in_guide}
          totalChapters={data.progress.total_chapters_in_guide}
        />
      </SlideIn>
    );
  }

  return (
    <>
      <SlideIn offset="50px">
        <FaireAcademyUpsell />
      </SlideIn>
      <Spacer height={{ mobileAndAbove: "4x", desktopAndAbove: "8x" }} />
      <SlideIn offset="70px">
        <Typography
          variant={{
            mobileAndAbove: "pageHeaderSerifRegular",
            desktopAndAbove: "displaySSerifRegular",
          }}
        >
          <StrictLocalMsg
            id="EwkFom"
            defaultMessage="Get started with one of our guides"
            description={{
              text: "Prompting customer to read a guide about starting a retail shop",
              img: "https://cdn.faire.com/fastly/7ea12df01ff4a012083fdf1538203f16b0e3ba9ee084ae195beae0a77cda9bc6.png",
            }}
          />
        </Typography>
        <Spacer height={{ mobileAndAbove: "3x", desktopAndAbove: "4x" }} />
        <AcademyGuideCards />
      </SlideIn>
    </>
  );
};

const FaireAcademyUpsellImage: React.FC = () => {
  const { storeType, shouldShowPartialRetailersBasedOnStoreType } =
    useGetStoreType();
  let imageSrc =
    "https://cdn.faire.com/fastly/70b8fad3bdeb12fb73ac52231167553c48c605a3e40afac5d30376a640b6d522.png";

  if (shouldShowPartialRetailersBasedOnStoreType) {
    if (storeType === StoreType.SPA_OR_SALON) {
      imageSrc =
        "https://cdn.faire.com/fastly/bf36ccb31d2de99c43013c0304c44a3b5c754bf0c80bed0a9ba8632f6b45e5e2.png";
    } else if (storeType === StoreType.CAFE_OR_RESTAURANT) {
      imageSrc =
        "https://cdn.faire.com/fastly/f386138d713dc34eb7e26dbd45399d84c771c829b40bb7ef99b37dce8d4f7458.png";
    }
  }

  return <StyledImage src={imageSrc} alt="" />;
};

const FaireAcademyUpsell: React.FC = () => {
  const { shouldShowPartialRetailersBasedOnStoreType } = useGetStoreType();

  return (
    <ResponsiveRowOrColumnReverse>
      <FaireAcademyUpsellImage />
      <Spacer size={{ mobileAndAbove: "4x", desktopAndAbove: "8x" }} />
      <Column justify="center">
        <Typography
          variant={{
            mobileAndAbove: "displaySSerifRegular",
            desktopAndAbove: "displayMSerifRegular",
          }}
        >
          {shouldShowPartialRetailersBasedOnStoreType ? (
            <StrictLocalMsg
              id="2oJUru"
              defaultMessage="Learn the retail fundamentals"
              description={{
                text: "Learn the fundamentals of opening a new retailer store with Faire Academy",
                img: "https://cdn.faire.com/fastly/f72537172f4eef81a09a792facab8256b550a8378cc74c977d95ec744d87a8cb.png",
              }}
            />
          ) : (
            <StrictLocalMsg
              id="mYYURc"
              defaultMessage="Master the fundamentals of opening a retail store"
              description={{
                text: "Learn the fundamentals of opening a new retailer store with Faire Academy",
                img: "https://cdn.faire.com/fastly/7ea12df01ff4a012083fdf1538203f16b0e3ba9ee084ae195beae0a77cda9bc6.png",
              }}
            />
          )}
        </Typography>
        <Spacer height={{ mobileAndAbove: "2x", desktopAndAbove: "3x" }} />
        <Typography
          variant={{
            mobileAndAbove: "paragraphSansRegular",
            desktopAndAbove: "subheadingSansRegular",
          }}
        >
          {shouldShowPartialRetailersBasedOnStoreType ? (
            <StrictLocalMsg
              id="GljILl"
              description={{
                text: "Description of what Faire Academy offers",
                img: "https://cdn.faire.com/fastly/f72537172f4eef81a09a792facab8256b550a8378cc74c977d95ec744d87a8cb.png",
              }}
              defaultMessage="We’ve got everything you need to know when it comes to starting a retail business (and none of what you don’t). Check out our 20+ guides covering all the basics, from how to get funding, to how to do inventory planning, and more."
            />
          ) : (
            <StrictLocalMsg
              id="CWw73M"
              description={{
                text: "Description of what Faire Academy offers",
                img: "https://cdn.faire.com/fastly/63baa66d4035bc9c55c6259ef83797b6eea1587b1ee648811a4aa9cb06db21a0.png",
              }}
              defaultMessage="We’ve got everything you need to know when it comes to opening a store (and none of what you don’t). Check out our guides covering all the essentials, from how to get funding, to how to do inventory planning, and more."
            />
          )}
        </Typography>
        <Spacer height={{ mobileAndAbove: "2x", desktopAndAbove: "3x" }} />
        <ExploreAllGuidesButton variant="secondary" />
      </Column>
    </ResponsiveRowOrColumnReverse>
  );
};

const AcademyGuideCards: React.FC = () => {
  const { isMobile, isTablet } = useViewport();
  const { pageState } = useGetFaireAcademyPageState();
  const guidesWithLikes = useGuidesWithLikes();

  return (
    <NegativeMargin>
      <Carousel
        name="FAIRE-ACADEMY-GUIDES"
        items={guidesWithLikes}
        itemsPerSlide={isMobile ? 1 : 3}
        peekNext={isMobile}
        renderItem={(guide) => {
          return (
            <AcademyGuideCardSmall
              key={guide.title}
              guide={guide}
              onClick={() =>
                trackOpenWithFaireIndividualGuideCtaClick(
                  pageState,
                  guide.title,
                  OWF_SECTION_NAME.MASTER_FUNDAMENTALS,
                  ""
                )
              }
              useSmallHeight={isTablet}
              fullWidth
            />
          );
        }}
      />
    </NegativeMargin>
  );
};

const ResponsiveRowOrColumnReverse = styled(Flex).attrs({
  direction: {
    mobileAndAbove: "column-reverse",
    tabletAndAbove: "row",
  },
})``;

// For carousel only, we want the peek to bleed a little bit more
const NegativeMargin = styled.div`
  ${responsiveMediaQueries.mobile} {
    margin: 0 -24px;
  }
`;

const StyledImage = styled.img`
  width: 455px;
  height: 455px;

  ${responsiveMediaQueries.tablet} {
    width: 300px;
    height: 300px;
  }
  ${responsiveMediaQueries.mobile} {
    width: 100%;
    height: 100%;
  }
`;
